import { EditableTitle } from '@/components/Title/EditableTitle';
import { toast } from '@/components/toast';
import { PolotnoStoreKeys, useCurrentProjectId, usePolotnoState } from '@/pages/AdvancedEditor/hooks';
import useGetProject from '@/pages/AdvancedEditor/hooks/useGetProject';
import { useAnalytics } from '@/services/analytics';
import { updateVideo } from '@/services/api';
import { DEFAULT_VIDEO_NAME } from '@/state/presenters/presenters';
import { LibraryItemType } from '@d-id/types';
import styled from '@emotion/styled';
import { ListItem, ListItemText } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useQueryClient } from 'react-query';

const VideoNameItem = styled(ListItem)`
    display: table;
    flex-direction: column;
`;

const StyledListItemText = styled(ListItemText)`
    & .MuiTypography-root {
        font-size: 1.4rem;
        font-weight: 600;
        text-align: left;
    }
`;

const StyledEditableTitle = styled(EditableTitle)`
    font-size: 1.6rem;
    line-height: 2rem;
    font-weight: 400;
    overflow: visible;
    word-break: break-word;
    word-wrap: break-word;
    max-width: 100%;
    height: 100%;
    margin-top: 2px;
    min-width: 231px;
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 8px;
    margin-bottom: 6px;
    max-width: 231px;
`;

export const VideoTitle = observer(() => {
    const mixpanel = useAnalytics();
    const queryClient = useQueryClient();

    const projectId = useCurrentProjectId();
    const { data: project } = useGetProject(projectId);

    const [videoName, setVideoName] = usePolotnoState(PolotnoStoreKeys.creativeVideoName);

    const updateName = async (name: string) => {
        try {
            await updateVideo(projectId, { name: name.trim() || DEFAULT_VIDEO_NAME }, LibraryItemType.PROJECT);
            queryClient.invalidateQueries({ queryKey: [`get-project-${projectId}`] });
        } catch (e) {
            toast.error({ title: 'Failed to update video name, please try again' });
        }
    };

    const handleBlur = async (value: string) => {
        if (value !== project?.name) {
            mixpanel?.track('project-name', { event: 'click', value, from: 'generate' });
            setVideoName(value);
            updateName(value);
        }
    };

    return (
        <VideoNameItem disablePadding>
            <StyledListItemText primary="Video name" />
            <TitleContainer>
                <StyledEditableTitle
                    testId="editor-dropdown-video-title-input"
                    value={videoName}
                    onChange={setVideoName}
                    placeholder="Type your video title here"
                    color="black"
                    onBlur={handleBlur}
                    autofocus
                    showIcon
                />
            </TitleContainer>
        </VideoNameItem>
    );
});
